import { useCallback, useMemo } from 'react';

import { useGetAvailableBatches } from '../api';
import { FILTER_TYPES, filterMetaData } from '../constants';
import type { ProcessedCourseWiseBatchesData, VariantInfo } from '../types';
import {
  processCourseWiseBatchesData,
  extractDayCombinations,
  getOngoingVoucherDisplay,
} from '../utils';

interface UseBatchDataProps {
  profileId: string;
  variantIds: VariantInfo[];
  country: string;
}

interface TagDetail {
  color: string;
  name: string;
  text: string;
  voucherLabel?: string;
}

interface UseBatchDataReturn {
  processedBatchesData: ProcessedCourseWiseBatchesData;
  dayCombinations: Record<string, string[]>;
  isLoading: boolean;
  hasError: boolean;
  loadingVariants: VariantInfo[];
  errorVariants: VariantInfo[];
  successVariants: VariantInfo[];
  progress: {
    completed: number;
    total: number;
    percentage: number;
  };
  refetch: () => void;
  getTagDetails: (tags: string[]) => TagDetail[];
  voucherLabel: string;
}

export const useBatchData = ({
  profileId,
  variantIds,
  country,
}: UseBatchDataProps): UseBatchDataReturn => {
  // Use the multiple variants hook to handle all variants
  const {
    courseWiseBatchesData,
    ongoingVoucher,
    isLoading,
    hasError,
    loadingVariants,
    errorVariants,
    successVariants,
    progress,
    refetch,
  } = useGetAvailableBatches({
    profileId,
    variants: variantIds,
    country,
  });

  const processedBatchesData: ProcessedCourseWiseBatchesData = useMemo(
    () => processCourseWiseBatchesData(courseWiseBatchesData ?? {}),
    [courseWiseBatchesData]
  );

  const dayCombinations: Record<string, string[]> = useMemo(
    () => extractDayCombinations(processedBatchesData),
    [processedBatchesData]
  );

  const voucherLabel = useMemo(
    () => getOngoingVoucherDisplay(ongoingVoucher, country).label,
    [ongoingVoucher, country]
  );

  const getTagDetails = useCallback(
    (tags: string[]): TagDetail[] => {
      return tags.map(tag => {
        const filterMeta = filterMetaData.find(
          filter => filter.filterName === tag
        );
        if (!filterMeta) {
          return {
            color: 'var(--color-magical-moonlight-d3)',
            name: tag,
            text: tag,
          };
        }
        if (tag === FILTER_TYPES.ONGOING) {
          return {
            ...filterMeta.tagDetails,
            text: `💸 Get ${voucherLabel} voucher`,
            voucherLabel,
          };
        }
        return filterMeta.tagDetails;
      });
    },
    [voucherLabel]
  );

  return {
    processedBatchesData,
    dayCombinations,
    isLoading,
    hasError,
    loadingVariants,
    errorVariants,
    successVariants,
    progress,
    refetch,
    getTagDetails,
    voucherLabel,
  };
};
